/**
 * The projects list: one row per project, with how far each has come.
 *
 * It owns its own fetch rather than being handed the list. The section above it
 * only knows the URL, and a list that reloads whenever it is mounted is exactly
 * what the Back button wants: a milestone ticked while the page was open shows
 * on the row the moment the client returns to it.
 */
import { useEffect, useState } from "react";
import { AlertCircle, FolderKanban, RefreshCw } from "lucide-react";
import { Badge, Button, Card } from "@wa/ui";
import { useT, useI18n } from "../../../i18n/I18nProvider";
import { portal, type PortalProject } from "../../../lib/portal";
import { fmtDate, genericStatusVariant } from "../../../lib/format";
import { EmptyState, ListSkeleton, PageHead, RowButton } from "../../ui";
import { ProgressBar } from "./ProgressBar";

export function ProjectList({ onOpen }: { onOpen: (id: string) => void }) {
  const t = useT();
  const { locale } = useI18n();
  const [projects, setProjects] = useState<PortalProject[] | null>(null);
  const [failed, setFailed] = useState(false);

  function load() {
    setFailed(false);
    setProjects(null);
    // A failed read says so, rather than passing for "you have no projects".
    void portal.projects().then(setProjects).catch(() => setFailed(true));
  }
  useEffect(load, []);

  return (
    <div>
      <PageHead title={t("portal.projects.title")} description={t("portal.projects.lead")} />

      <Card className="overflow-hidden">
        {failed ? (
          <EmptyState
            icon={AlertCircle}
            title={t("portal.loadFailed")}
            action={
              <Button variant="outline" onClick={load}>
                <RefreshCw />
                {t("portal.retry")}
              </Button>
            }
          />
        ) : !projects ? (
          <ListSkeleton rows={3} label={t("common.loading")} />
        ) : projects.length === 0 ? (
          <EmptyState icon={FolderKanban} title={t("portal.projects.emptyTitle")} body={t("portal.projects.emptyBody")} />
        ) : (
          <ul className="divide-y divide-border">
            {projects.map((p) => (
              <li key={p.id}>
                <RowButton onClick={() => onOpen(p.id)} className="flex-col items-stretch gap-3 py-5">
                  <span className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-[15px] font-semibold">{p.name}</span>
                    <Badge variant={genericStatusVariant(p.status)}>{t(`portal.projectStatus.${p.status}`)}</Badge>
                  </span>
                  <span className="flex items-center gap-3">
                    <ProgressBar pct={p.progressPct} />
                    <span className="w-10 shrink-0 text-right text-xs font-semibold tabular-nums">{p.progressPct}%</span>
                  </span>
                  <span className="flex flex-wrap gap-x-4 gap-y-1 text-foreground/70">
                    <span>{t("portal.projects.tasks", { n: p._count.tasks })}</span>
                    <span>{t("portal.projects.milestones", { n: p._count.milestones })}</span>
                    {p.deadline && <span>{t("portal.projects.deadline")}: {fmtDate(p.deadline, locale)}</span>}
                  </span>
                </RowButton>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
